import { additionalBarcodes } from '../../../packages/shared/product-barcodes';
import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  DataTable,
  Banner,
  Tabs,
  Thumbnail,
  DescriptionList,
  Divider,
  Spinner,
  Pagination,
  Select,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import ProductLabels from '../components/labels/ProductLabels';

interface Product {
  id: number;
  shopify_product_id: string;
  shopify_variant_id: string;
  sku: string | null;
  barcode: string | null;
  barcodes?: string;
  title: string;
  description: string | null;
  vendor: string | null;
  image_url: string | null;
  hs_code: string | null;
  country_of_origin: string | null;
  updated_at: string | null;
  updated_by_name: string | null;
  requires_shipping: number;
  track_inventory: number;
}

interface StockLevel {
  stock_level_id: number;
  quantity: number;
  reserved: number;
  available: number;
  location_id: number;
  location_code: string;
  zone: string | null;
  location_type: string;
  warehouse_id: number;
  warehouse_name: string;
}

interface Totals {
  total_quantity: number;
  allocated: number;
  available: number;
}

interface Movement {
  id: number;
  type: string;
  quantity: number;
  running_total: number;
  reference: string | null;
  notes: string | null;
  staff_id: string | null;
  staff_name: string | null;
  created_at: string;
  from_location: string | null;
  from_warehouse: string | null;
  to_location: string | null;
  to_warehouse: string | null;
}

interface OrderAllocation {
  order_id: number;
  order_number: string;
  customer_name: string | null;
  order_status: string;
  delivery_date: string | null;
  required_qty: number;
  allocated_qty: number;
  picked_qty: number;
  backordered_qty: number;
  order_item_id: number;
}

interface AllocationTotals {
  required: number;
  allocated: number;
  picked: number;
  backordered: number;
  pending_allocation: number;
}

export default function ProductDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { fetchApi, error } = useApi();

  const [product, setProduct] = useState<Product | null>(null);
  const [stockLevels, setStockLevels] = useState<StockLevel[]>([]);
  const [totals, setTotals] = useState<Totals>({ total_quantity: 0, allocated: 0, available: 0 });
  const [loading, setLoading] = useState(true);

  // Movements tab state
  const [movements, setMovements] = useState<Movement[]>([]);
  const [movementsPage, setMovementsPage] = useState(1);
  const [movementsTotalPages, setMovementsTotalPages] = useState(1);
  const [movementsTotal, setMovementsTotal] = useState(0);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [movementTypeFilter, setMovementTypeFilter] = useState('');

  // Order allocations state
  const [allocations, setAllocations] = useState<OrderAllocation[]>([]);
  const [allocationTotals, setAllocationTotals] = useState<AllocationTotals>({
    required: 0,
    allocated: 0,
    picked: 0,
    backordered: 0,
    pending_allocation: 0,
  });
  const [allocationsLoading, setAllocationsLoading] = useState(false);

  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    loadProduct();
    loadAllocations();
  }, [productId]);

  useEffect(() => {
    if (selectedTab === 1) {
      loadMovements();
    }
  }, [selectedTab, movementsPage, movementTypeFilter]);

  async function loadAllocations() {
    setAllocationsLoading(true);
    const data = await fetchApi<{
      allocations: OrderAllocation[];
      totals: AllocationTotals;
    }>(`/products/${productId}/allocations`);

    if (data) {
      setAllocations(data.allocations || []);
      setAllocationTotals(data.totals);
    }
    setAllocationsLoading(false);
  }

  async function loadProduct() {
    setLoading(true);
    const data = await fetchApi<{
      product: Product;
      stock_levels: StockLevel[];
      totals: Totals;
    }>(`/products/${productId}`);

    if (data) {
      setProduct(data.product);
      setStockLevels(data.stock_levels || []);
      setTotals(data.totals);
    }
    setLoading(false);
  }

  const loadMovements = useCallback(async () => {
    setMovementsLoading(true);
    let url = `/products/${productId}/movements?page=${movementsPage}&limit=20`;
    if (movementTypeFilter) {
      url += `&type=${movementTypeFilter}`;
    }

    const data = await fetchApi<{
      movements: Movement[];
      total: number;
      totalPages: number;
    }>(url);

    if (data) {
      setMovements(data.movements || []);
      setMovementsTotal(data.total);
      setMovementsTotalPages(data.totalPages);
    }
    setMovementsLoading(false);
  }, [productId, movementsPage, movementTypeFilter, fetchApi]);

  const tabs = [
    { id: 'details', content: 'Details', panelID: 'details-panel' },
    { id: 'history', content: 'History', panelID: 'history-panel' },
  ];

  const getMovementTypeBadge = (type: string) => {
    const config: Record<string, { tone: 'success' | 'warning' | 'info' | 'critical' | 'attention'; label: string }> = {
      receive: { tone: 'success', label: 'Receive' },
      dispatch: { tone: 'critical', label: 'Dispatch' },
      transfer: { tone: 'info', label: 'Transfer' },
      adjust_in: { tone: 'success', label: 'Adjust +' },
      adjust_out: { tone: 'warning', label: 'Adjust -' },
      count: { tone: 'attention', label: 'Count' },
    };
    const { tone, label } = config[type] || { tone: 'info', label: type };
    return <Badge tone={tone}>{label}</Badge>;
  };

  const getOrderStatusBadge = (status: string) => {
    const config: Record<string, { tone: 'success' | 'warning' | 'info' | 'critical' | 'attention'; label: string }> = {
      new: { tone: 'info', label: 'New' },
      allocated: { tone: 'attention', label: 'Allocated' },
      picking: { tone: 'warning', label: 'Picking' },
      picked: { tone: 'success', label: 'Picked' },
      packed: { tone: 'success', label: 'Packed' },
      ready: { tone: 'success', label: 'Ready' },
    };
    const { tone, label } = config[status] || { tone: 'info', label: status };
    return <Badge tone={tone}>{label}</Badge>;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const stripHtml = (html: string | null) => {
    if (!html) return null;
    return html.replace(/<[^>]*>/g, '').trim();
  };

  if (loading) {
    return (
      <Page title="Product Details">
        <Layout>
          <Layout.Section>
            <Card>
              <InlineStack align="center" blockAlign="center">
                <Spinner size="large" />
              </InlineStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (!product) {
    return (
      <Page
        title="Product Details"
        backAction={{ content: 'Products', onAction: () => navigate('/products') }}
      >
        <Banner tone="critical">Product not found</Banner>
      </Page>
    );
  }

  return (
    <Page
      title={product.title}
      backAction={{ content: 'Products', onAction: () => navigate('/products') }}
      subtitle={product.sku ? `SKU: ${product.sku}` : undefined}
    >
      <Layout>
        <Layout.Section><ProductLabels product={product}/></Layout.Section>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab} />
        </Layout.Section>

        {/* DETAILS TAB */}
        {selectedTab === 0 && (
          <>
            {/* Product Info */}
            <Layout.Section>
              <Card>
                <InlineStack gap="600" align="start" blockAlign="start">
                  {/* Product Image */}
                  <div style={{ flexShrink: 0 }}>
                    {product.image_url ? (
                      <Thumbnail
                        source={product.image_url}
                        alt={product.title}
                        size="large"
                      />
                    ) : (
                      <div
                        style={{
                          width: 100,
                          height: 100,
                          backgroundColor: '#f4f6f8',
                          borderRadius: 8,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Text as="span" tone="subdued">No image</Text>
                      </div>
                    )}
                  </div>

                  {/* Product Details */}
                  <BlockStack gap="400" inlineAlign="start">
                    <Text as="h2" variant="headingLg">{product.title}</Text>

                    <DescriptionList
                      items={[
                        { term: 'SKU', description: product.sku || '-' },
                        { term: 'Barcodes', description: [...new Set([product.barcode,...additionalBarcodes(product)].filter(Boolean))].join(', ') || '-' },
                        { term: 'Supplier', description: product.vendor || '-' },
                        { term: 'HS Code', description: product.hs_code || '-' },
                        { term: 'Country of Origin', description: product.country_of_origin || '-' },
                      ]}
                    />

                    {product.description && (
                      <>
                        <Divider />
                        <BlockStack gap="200">
                          <Text as="h3" variant="headingSm">Description</Text>
                          <Text as="p" tone="subdued">{stripHtml(product.description)}</Text>
                        </BlockStack>
                      </>
                    )}
                  </BlockStack>
                </InlineStack>
              </Card>
            </Layout.Section>

            {/* Stock Summary */}
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">Stock Summary</Text>

                  <InlineStack gap="800">
                    <BlockStack gap="100">
                      <Text as="span" tone="subdued">Total Quantity</Text>
                      <Text as="p" variant="headingLg">{totals.total_quantity}</Text>
                    </BlockStack>
                    <BlockStack gap="100">
                      <Text as="span" tone="subdued">Allocated</Text>
                      <Text as="p" variant="headingLg">{totals.allocated}</Text>
                    </BlockStack>
                    <BlockStack gap="100">
                      <Text as="span" tone="subdued">Available</Text>
                      <Text as="p" variant="headingLg">
                        <Badge tone={totals.available > 0 ? 'success' : 'critical'}>
                          {String(totals.available)}
                        </Badge>
                      </Text>
                    </BlockStack>
                  </InlineStack>
                </BlockStack>
              </Card>
            </Layout.Section>

            {/* Stock by Location */}
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">Stock by Location</Text>

                  {stockLevels.length > 0 ? (
                    <DataTable
                      columnContentTypes={['text', 'text', 'text', 'numeric', 'numeric', 'numeric']}
                      headings={['Warehouse', 'Location', 'Zone', 'Quantity', 'Reserved', 'Available']}
                      rows={stockLevels.map((level) => [
                        level.warehouse_name,
                        level.location_code,
                        level.zone || '-',
                        level.quantity.toString(),
                        level.reserved.toString(),
                        <Badge tone={level.available > 0 ? 'success' : 'critical'} key={level.stock_level_id}>
                          {String(level.available)}
                        </Badge>,
                      ])}
                    />
                  ) : (
                    <Text as="p" tone="subdued">No stock locations configured for this product</Text>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>

            {/* Order Allocations */}
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <InlineStack align="space-between">
                    <Text as="h2" variant="headingMd">Order Allocations</Text>
                    {allocations.length > 0 && (
                      <InlineStack gap="400">
                        <Text as="span" tone="subdued">
                          Required: {allocationTotals.required}
                        </Text>
                        <Text as="span" tone="subdued">
                          Allocated: {allocationTotals.allocated}
                        </Text>
                        {allocationTotals.pending_allocation > 0 && (
                          <Badge tone="warning">
                            {`${allocationTotals.pending_allocation} pending`}
                          </Badge>
                        )}
                      </InlineStack>
                    )}
                  </InlineStack>

                  {allocationsLoading ? (
                    <InlineStack align="center">
                      <Spinner size="small" />
                    </InlineStack>
                  ) : allocations.length > 0 ? (
                    <DataTable
                      columnContentTypes={['text', 'text', 'text', 'numeric', 'numeric', 'text']}
                      headings={['Order', 'Customer', 'Delivery Date', 'Required', 'Allocated', 'Status']}
                      rows={allocations.map((alloc) => [
                        <span
                          key={`order-${alloc.order_id}`}
                          style={{ cursor: 'pointer', color: '#2c6ecb', textDecoration: 'underline' }}
                          onClick={() => navigate(`/orders/${alloc.order_id}`)}
                        >
                          #{alloc.order_number}
                        </span>,
                        alloc.customer_name || '-',
                        alloc.delivery_date
                          ? new Date(alloc.delivery_date).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                            })
                          : '-',
                        alloc.required_qty.toString(),
                        alloc.allocated_qty === alloc.required_qty ? (
                          <Badge tone="success" key={`alloc-${alloc.order_item_id}`}>
                            {String(alloc.allocated_qty)}
                          </Badge>
                        ) : alloc.allocated_qty > 0 ? (
                          <Badge tone="warning" key={`alloc-${alloc.order_item_id}`}>
                            {`${alloc.allocated_qty}/${alloc.required_qty}`}
                          </Badge>
                        ) : (
                          <Badge tone="critical" key={`alloc-${alloc.order_item_id}`}>
                            0
                          </Badge>
                        ),
                        getOrderStatusBadge(alloc.order_status),
                      ])}
                    />
                  ) : (
                    <Text as="p" tone="subdued">No orders currently require this product</Text>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>

            {/* Metadata */}
            <Layout.Section>
              <Card>
                <BlockStack gap="200">
                  <Text as="h2" variant="headingMd">Metadata</Text>
                  <InlineStack gap="600">
                    <BlockStack gap="100">
                      <Text as="span" tone="subdued">Last Updated</Text>
                      <Text as="p">
                        {product.updated_at ? formatDate(product.updated_at) : '-'}
                      </Text>
                    </BlockStack>
                    <BlockStack gap="100">
                      <Text as="span" tone="subdued">Updated By</Text>
                      <Text as="p">{product.updated_by_name || '-'}</Text>
                    </BlockStack>
                    <BlockStack gap="100">
                      <Text as="span" tone="subdued">Tracks Inventory</Text>
                      <Badge tone={product.track_inventory ? 'success' : 'warning'}>
                        {product.track_inventory ? 'Yes' : 'No'}
                      </Badge>
                    </BlockStack>
                  </InlineStack>
                </BlockStack>
              </Card>
            </Layout.Section>
          </>
        )}

        {/* HISTORY TAB */}
        {selectedTab === 1 && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between">
                  <Text as="h2" variant="headingMd">Stock Movements</Text>
                  <InlineStack gap="200">
                    <Select
                      label=""
                      labelHidden
                      options={[
                        { label: 'All Types', value: '' },
                        { label: 'Receive', value: 'receive' },
                        { label: 'Dispatch', value: 'dispatch' },
                        { label: 'Transfer', value: 'transfer' },
                        { label: 'Adjust +', value: 'adjust_in' },
                        { label: 'Adjust -', value: 'adjust_out' },
                        { label: 'Count', value: 'count' },
                      ]}
                      value={movementTypeFilter}
                      onChange={(value) => {
                        setMovementTypeFilter(value);
                        setMovementsPage(1);
                      }}
                    />
                    <Text as="span" tone="subdued">
                      {movementsTotal} movement{movementsTotal !== 1 ? 's' : ''}
                    </Text>
                  </InlineStack>
                </InlineStack>

                {movementsLoading ? (
                  <InlineStack align="center">
                    <Spinner size="small" />
                  </InlineStack>
                ) : movements.length > 0 ? (
                  <>
                    <DataTable
                      columnContentTypes={['text', 'text', 'numeric', 'numeric', 'text', 'text', 'text']}
                      headings={['Date', 'Type', 'Qty', 'Running Total', 'Location', 'Reference', 'User']}
                      rows={movements.map((m) => [
                        formatDate(m.created_at),
                        getMovementTypeBadge(m.type),
                        m.type === 'dispatch' || m.type === 'adjust_out'
                          ? `-${m.quantity}`
                          : `+${m.quantity}`,
                        m.running_total.toString(),
                        m.to_location
                          ? `${m.to_warehouse || ''} ${m.to_location}`.trim()
                          : m.from_location
                            ? `${m.from_warehouse || ''} ${m.from_location}`.trim()
                            : '-',
                        m.reference || '-',
                        m.staff_name || '-',
                      ])}
                    />

                    {movementsTotalPages > 1 && (
                      <InlineStack align="center">
                        <Pagination
                          hasPrevious={movementsPage > 1}
                          hasNext={movementsPage < movementsTotalPages}
                          onPrevious={() => setMovementsPage((p) => p - 1)}
                          onNext={() => setMovementsPage((p) => p + 1)}
                          label={`Page ${movementsPage} of ${movementsTotalPages}`}
                        />
                      </InlineStack>
                    )}
                  </>
                ) : (
                  <Text as="p" tone="subdued">No stock movements recorded for this product</Text>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>
        )}
      </Layout>
    </Page>
  );
}
